// Spaltenbreite ziehen und Spalten per Ziehen im Kopf verschieben, gemeinsam für Kontakte, Aktivitäten und die Opportunities-Liste
// (Feinschliff 16.10.2026; die Lead-Tabelle hat dieselbe Logik inline). Breite: Griff am rechten Rand des Kopfes, Doppelklick
// setzt zurück; Verschieben: Spaltenkopf auf eine andere Spalte ziehen. Gespeichert wird von der Seite (je Nutzer und Liste).
import { useState, type CSSProperties, type DragEvent, type PointerEvent, type ReactNode } from "react";

export const MIN_BREITE = 64;
export const MAX_BREITE = 640;

interface Optionen {
  /** Breiten in px je Spaltenschlüssel (gespeichert); ohne Eintrag: Breite nach Inhalt */
  breiten?: Record<string, number>;
  /** Breite gezogen (px) oder per Doppelklick zurückgesetzt (null) */
  onBreite?: (key: string, px: number | null) => void;
  /** Spalte `von` wurde vor die Spalte `nach` gezogen */
  onVerschieben?: (von: string, nach: string) => void;
  /** Spalten, die weder verschoben werden noch Ziel sein können (Markierungsspalte, erste Spalte) */
  fest?: (key: string) => boolean;
}

export function useSpaltenZiehen({ breiten, onBreite, onVerschieben, fest }: Optionen) {
  const [live, setLive] = useState<{ key: string; px: number } | null>(null);
  const [zieht, setZieht] = useState<string | null>(null);
  const [ziel, setZiel] = useState<string | null>(null);
  const istFest = (key: string) => !!fest?.(key);
  const breiteVon = (key: string): number | undefined => (live?.key === key ? live.px : breiten?.[key]);

  /** Stil für th und td einer Spalte (feste Breite, wenn gezogen) */
  const zellStil = (key: string): CSSProperties | undefined => {
    const b = breiteVon(key);
    return b ? { width: b, minWidth: b, maxWidth: b } : undefined;
  };
  /** Stil für den Inhalt (`.zelle`), damit die Begrenzung der Seite die gezogene Breite nicht kappt */
  const inhaltStil = (key: string): CSSProperties | undefined => (breiteVon(key) ? { maxWidth: "none" } : undefined);

  const ziehen = (e: PointerEvent<HTMLSpanElement>, key: string) => {
    if (!onBreite) return;
    e.preventDefault();
    e.stopPropagation();
    const th = (e.currentTarget.closest("th") as HTMLElement | null) ?? e.currentTarget;
    const start = th.getBoundingClientRect().width;
    const x0 = e.clientX;
    let px = start;
    const zeiger = e.currentTarget;
    zeiger.setPointerCapture(e.pointerId);
    const bewegt = (m: globalThis.PointerEvent) => {
      px = Math.round(Math.max(MIN_BREITE, Math.min(MAX_BREITE, start + m.clientX - x0)));
      setLive({ key, px });
    };
    const fertig = () => {
      zeiger.removeEventListener("pointermove", bewegt);
      zeiger.removeEventListener("pointerup", fertig);
      zeiger.removeEventListener("pointercancel", fertig);
      setLive(null);
      if (px !== Math.round(start)) onBreite(key, px);
    };
    zeiger.addEventListener("pointermove", bewegt);
    zeiger.addEventListener("pointerup", fertig);
    zeiger.addEventListener("pointercancel", fertig);
  };

  /** Eigenschaften für das `th` einer Spalte (Ziehen zum Verschieben); `klasse` ist die Grundklasse */
  const kopfEigenschaften = (key: string, klasse: string): {
    className: string;
    style: CSSProperties | undefined;
    draggable: boolean;
    onDragStart: (e: DragEvent) => void;
    onDragOver: (e: DragEvent) => void;
    onDrop: (e: DragEvent) => void;
    onDragEnd: () => void;
  } => {
    const beweglich = !!onVerschieben && !istFest(key);
    return {
      className: `${klasse}${ziel === key && zieht !== key ? " tabelle__ziel" : ""}${zieht === key ? " tabelle__zieht" : ""}`.trim(),
      style: zellStil(key),
      draggable: beweglich,
      onDragStart: (e) => {
        if (!beweglich) return;
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", key);
        setZieht(key);
      },
      onDragOver: (e) => {
        if (zieht && !istFest(key)) {
          e.preventDefault();
          setZiel(key);
        }
      },
      onDrop: (e) => {
        e.preventDefault();
        if (zieht && onVerschieben && !istFest(key)) onVerschieben(zieht, key);
        setZieht(null);
        setZiel(null);
      },
      onDragEnd: () => {
        setZieht(null);
        setZiel(null);
      },
    };
  };

  /** Griff am rechten Rand des Kopfes */
  const griff = (key: string, label: string): ReactNode =>
    onBreite ? (
      <span
        className="tabelle__griff"
        role="separator"
        aria-orientation="vertical"
        aria-label={`Breite von ${label || "Spalte"} ändern`}
        title="Breite ziehen, Doppelklick: zurücksetzen"
        draggable={false}
        onPointerDown={(e) => ziehen(e, key)}
        onDoubleClick={(e) => (e.stopPropagation(), onBreite(key, null))}
      />
    ) : null;

  return { zellStil, inhaltStil, kopfEigenschaften, griff };
}
